import { useState, useEffect, useCallback } from 'react'
import { checkStorageOptions } from '../../Utils'
import type { StorageOptionsStatus } from '../../Constant'

type StorageReadyState = {
  isReady: boolean
  isChecking: boolean
  error: string | null
  storageStatuses: StorageOptionsStatus
}

export const useStorageReady = (): StorageReadyState => {
  const [state, setState] = useState<StorageReadyState>({
    isReady: false,
    isChecking: true,
    error: null,
    storageStatuses: {
      indexedDB: false,
      local: false,
      session: false,
      cookies: false,
    },
  })

  const checkStorageReadiness = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isChecking: true, error: null }))

    try {
      const basicStatuses = checkStorageOptions()

      // Test actual IndexedDB functionality
      let actualIndexedDBWorking = false
      if (basicStatuses.indexedDB) {
        try {
          const testDB = await new Promise<boolean>((resolve, reject) => {
            let isResolved = false
            const testRequest = indexedDB.open('storage-test', 1)
            const timeout = setTimeout(() => {
              if (!isResolved) {
                isResolved = true
                reject(new Error('IndexedDB test timeout'))
              }
            }, 3000) // 3 second timeout

            testRequest.onerror = () => {
              clearTimeout(timeout)
              if (!isResolved) {
                isResolved = true
                reject(testRequest.error || new Error('IndexedDB test failed'))
              }
            }

            testRequest.onsuccess = () => {
              clearTimeout(timeout)
              if (!isResolved) {
                isResolved = true
                try {
                  testRequest.result.close()
                  // Try to delete the test database
                  const deleteRequest = indexedDB.deleteDatabase('storage-test')
                  deleteRequest.onsuccess = () => resolve(true)
                  deleteRequest.onerror = () => resolve(true)
                } catch (error) {
                  console.error('IndexedDB test failed:', error)
                  actualIndexedDBWorking = false
                }
              }
            }

            testRequest.onupgradeneeded = () => {
              // Database upgrade needed, no action required for test
              if (!isResolved) {
                isResolved = true
                resolve(true)
              }
            }
          })

          actualIndexedDBWorking = testDB
        } catch (error) {
          actualIndexedDBWorking = false
        }
      }

      const finalStatuses = {
        ...basicStatuses,
        indexedDB: actualIndexedDBWorking,
      }

      const hasWorkingStorage =
        finalStatuses.indexedDB || finalStatuses.local || finalStatuses.session

      setState({
        isReady: hasWorkingStorage,
        isChecking: false,
        error: hasWorkingStorage ? null : 'All storage methods failed verification',
        storageStatuses: finalStatuses,
      })
    } catch (error) {
      setState({
        isReady: false,
        isChecking: false,
        error:
          error instanceof Error
            ? error.message
            : 'Unknown storage initialization error',
        storageStatuses: {
          indexedDB: false,
          local: false,
          session: false,
          cookies: false,
        },
      })
    }
  }, [])

  useEffect(() => {
    checkStorageReadiness()
  }, [checkStorageReadiness])

  return state
}
